import React, { useEffect, useState } from 'react';
import { getSettings, updateSettings } from '../api/settings';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
  fontFamily: 'inherit',
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: 'vertical', minHeight: '120px', lineHeight: 1.6,
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '5px',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '13px', fontWeight: 600, color: 'var(--text)',
  borderBottom: '1px solid var(--border)', paddingBottom: '10px', marginBottom: '16px',
};
const hintStyle: React.CSSProperties = {
  fontSize: '11px', color: 'var(--text3)', marginTop: '4px',
};

const TEMPLATE_VARS = [
  { key: '{{mandat_titel}}', desc: 'Titel des Verkaufsmandats' },
  { key: '{{objekt_adresse}}', desc: 'Adresse des Objekts' },
  { key: '{{kaufpreis}}', desc: 'Kaufpreis in EUR' },
  { key: '{{groesse}}', desc: 'Größe in m²' },
  { key: '{{property_type}}', desc: 'Objekttyp' },
  { key: '{{anrede_full}}', desc: 'Empfohlen — komplette Anrede inkl. Titel ("Sehr geehrte Frau Dr. Müller" / "Sehr geehrter Herr Prof. Dr. Schmidt" / "Sehr geehrte Damen und Herren")' },
  { key: '{{anrede}}', desc: 'Nur Anrede-Wort: Herr / Frau / Damen und Herren' },
  { key: '{{titel}}', desc: 'Akademischer Titel: Dr. / Prof. / Prof. Dr. (oder leer)' },
  { key: '{{name}}', desc: 'Nachname des Kontakts (ohne Vorname)' },
];

export function CommunicationPage() {
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSettings()
      .then(s => setSettings(s))
      .catch(() => setError('Daten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: string, value: string) =>
    setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError(null);
    try {
      await updateSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>Laden...</div>;
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-header-title">Kommunikation</div>
          <div className="page-header-sub">E-Mail-Versand, SMTP/IMAP & Vorlagen</div>
        </div>
      </div>
      <div className="content">
        <div style={{ maxWidth: '800px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '9px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Wird gespeichert...' : saved ? 'Gespeichert' : 'Speichern'}
            </button>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'grid', gap: '24px' }}>
            {/* SMTP */}
            <div className="card">
              <div className="card-header"><span className="card-title">E-Mail / SMTP</span></div>
              <div className="card-body">
                <div style={sectionTitle}>Serververbindung</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>SMTP-Host</label>
                    <input style={inputStyle} value={settings.smtp_host ?? ''} onChange={e => set('smtp_host', e.target.value)} placeholder="smtp.example.com" />
                  </div>
                  <div>
                    <label style={labelStyle}>Port</label>
                    <input style={inputStyle} value={settings.smtp_port ?? '587'} onChange={e => set('smtp_port', e.target.value)} placeholder="587" type="number" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>Benutzername (SMTP)</label>
                    <input style={inputStyle} value={settings.smtp_user ?? ''} onChange={e => set('smtp_user', e.target.value)} placeholder="user@example.com" autoComplete="username" />
                  </div>
                  <div>
                    <label style={labelStyle}>Passwort</label>
                    <input style={inputStyle} type="password" value={settings.smtp_password ?? ''} onChange={e => set('smtp_password', e.target.value)} placeholder="••••••••" autoComplete="current-password" />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '16px' }}>
                  <input type="checkbox" checked={settings.smtp_secure === 'true'} onChange={e => set('smtp_secure', e.target.checked ? 'true' : 'false')} />
                  <span style={{ fontSize: '13px' }}>SSL/TLS (Port 465)</span>
                </label>

                <div style={sectionTitle}>IMAP (Posteingang + Gesendet)</div>
                <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius)', marginBottom: '12px', fontSize: '12px', color: 'var(--text3)' }}>
                  IMAP wird für den Abruf empfangener Antworten und das Kopieren gesendeter E-Mails in den Gesendet-Ordner verwendet.
                  Benutzer/Passwort optional — bleibt leer, wenn identisch mit SMTP.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <label style={labelStyle}>IMAP-Host</label>
                    <input style={inputStyle} value={settings.imap_host ?? ''} onChange={e => set('imap_host', e.target.value)} placeholder="imap.ionos.de" />
                  </div>
                  <div>
                    <label style={labelStyle}>Port</label>
                    <input style={inputStyle} value={settings.imap_port ?? '993'} onChange={e => set('imap_port', e.target.value)} placeholder="993" type="number" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>IMAP-Benutzer (optional)</label>
                    <input style={inputStyle} value={settings.imap_user ?? ''} onChange={e => set('imap_user', e.target.value)} placeholder="leer = SMTP-Benutzer" autoComplete="username" />
                  </div>
                  <div>
                    <label style={labelStyle}>IMAP-Passwort (optional)</label>
                    <input style={inputStyle} type="password" value={settings.imap_password ?? ''} onChange={e => set('imap_password', e.target.value)} placeholder="leer = SMTP-Passwort" autoComplete="current-password" />
                  </div>
                </div>

                <div style={sectionTitle}>Absender</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={labelStyle}>Absender-Name</label>
                    <input style={inputStyle} value={settings.smtp_from_name ?? ''} onChange={e => set('smtp_from_name', e.target.value)} placeholder="Rubin Real Estate" />
                  </div>
                  <div>
                    <label style={labelStyle}>Absender-E-Mail</label>
                    <input style={inputStyle} type="email" value={settings.smtp_from_email ?? ''} onChange={e => set('smtp_from_email', e.target.value)} placeholder="info@rubinrealestate.de" />
                  </div>
                </div>
              </div>
            </div>

            {/* Vorlage */}
            <div className="card">
              <div className="card-header"><span className="card-title">E-Mail-Vorlage (Anschreiben)</span></div>
              <div className="card-body">
                <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius)', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                    Verfügbare Platzhalter
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {TEMPLATE_VARS.map(v => (
                      <span key={v.key} title={v.desc} style={{
                        padding: '2px 8px', background: 'var(--accent-light)', color: 'var(--accent)',
                        borderRadius: '4px', fontSize: '11px', fontFamily: 'DM Mono, monospace',
                      }}>
                        {v.key}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>Betreff</label>
                  <input style={inputStyle} value={settings.email_subject_template ?? ''} onChange={e => set('email_subject_template', e.target.value)} placeholder="Exposé: {{mandat_titel}} – {{objekt_adresse}}" />
                </div>
                <div>
                  <label style={labelStyle}>Anschreiben-Text</label>
                  <textarea style={textareaStyle} value={settings.email_body_template ?? ''} onChange={e => set('email_body_template', e.target.value)} rows={12} />
                  <div style={hintStyle}>Platzhalter werden beim Versand automatisch ersetzt.</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 28px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Wird gespeichert...' : saved ? 'Gespeichert' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
